import React, { useMemo } from "react";
import { CustomTable } from "../../../components/Table";
import { createColumnHelper } from "@tanstack/react-table";
import sortIcon from "../../../assets/sort.svg";

export interface CreditNoteRow {
  creditNoteRaisedOn?: string | number;
  creditNoteNumber?: string | number;
  amount?: string | number;
  status?: string;
  invoiceNumber?:
    | string
    | number
    | Array<string | number | Record<string, any>>;
  transactionId?: string | number | Record<string, any>;
  downloadUrl?: string;
  [key: string]: any;
}

export const DEFAULT_CREDIT_NOTE_DUMMY_ROWS: CreditNoteRow[] = [
  {
    creditNoteRaisedOn: "23 May 2023",
    creditNoteNumber: "12112024",
    amount: 5000,
    status: "Pending",
    invoiceNumber: "-",
    transactionId: "aa145872-5eb4-4764-975e-821f0bd8d48e",
    downloadUrl: "https://example.com/credit-note-12112024.pdf",
  },
  {
    creditNoteRaisedOn: "23 May 2023",
    creditNoteNumber: "12112032",
    amount: 3000,
    status: "Pending",
    invoiceNumber: "-",
    transactionId: "aa145872-5eb4-4764-975e-821f0bd8d48e",
    downloadUrl: "https://example.com/credit-note-12112032.pdf",
  },
  {
    creditNoteRaisedOn: "23 May 2023",
    creditNoteNumber: "12120233",
    amount: 6450,
    status: "Closed",
    invoiceNumber: "34567890",
    transactionId: "-",
    downloadUrl: "https://example.com/credit-note-12120233.pdf",
  },
  {
    creditNoteRaisedOn: "23 May 2023",
    creditNoteNumber: "12112032",
    amount: 3000,
    status: "Pending",
    invoiceNumber: "-",
    transactionId: "aa145872-5eb4-4764-975e-821f0bd8d48e",
    downloadUrl: "https://example.com/credit-note-12112032-v2.pdf",
  },
  {
    creditNoteRaisedOn: "23 May 2023",
    creditNoteNumber: "12120233",
    amount: 6450,
    status: "Pending",
    invoiceNumber: "34567890",
    transactionId: "-",
    downloadUrl: "https://example.com/credit-note-12120233-v2.pdf",
  },
];

interface ICreditNoteDataProps {
  creditData?: CreditNoteRow[];
}

const CreditNoteData: React.FunctionComponent<ICreditNoteDataProps> = (
  props
) => {
  const columnsHelper = createColumnHelper<any>();
  const creditData: CreditNoteRow[] = props?.creditData || [];

  const hasValue = (value: any) =>
    value !== undefined && value !== null && value !== "";

  const getCellValues = (
    value: any,
    preferredKeys: string[] = []
  ): string[] => {
    if (!hasValue(value)) return [];

    if (Array.isArray(value)) {
      return value.reduce<string[]>(
        (values, v) => [...values, ...getCellValues(v, preferredKeys)],
        []
      );
    }

    if (typeof value === "object") {
      const fallbackKeys = [
        ...preferredKeys,
        "transactionId",
        "transactionID",
        "txnId",
        "transaction_id",
        "invoiceNumber",
        "invoiceNo",
        "invoice_id",
        "creditNoteNumber",
        "creditNoteNo",
        "_id",
      ];

      for (const key of fallbackKeys) {
        const nestedValue = value?.[key];
        if (nestedValue !== value && hasValue(nestedValue)) {
          const formattedValues = getCellValues(nestedValue, preferredKeys);
          if (formattedValues.length > 0) return formattedValues;
        }
      }

      return [];
    }

    return String(value)
      .split(",")
      .map((v) => v.trim())
      .filter((v) => v && v !== "-");
  };

  const formatCellValue = (
    value: any,
    preferredKeys: string[] = []
  ): string => {
    const values = getCellValues(value, preferredKeys);
    return values.length > 0 ? values.join(", ") : "-";
  };

  const formatRaisedOn = (value: any) => {
    if (!value) return "-";
    if (typeof value === "string") return value;
    const epoch = Number(value);
    if (Number.isNaN(epoch)) return "-";
    return new Intl.DateTimeFormat("en-GB", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    }).format(new Date(epoch));
  };

  const formatAmount = (value: any) => {
    if (value === undefined || value === null || value === "") return "-";
    const num = Number(value);
    if (Number.isNaN(num)) return `₹ ${value}`;
    return `₹ ${num.toLocaleString("en-IN")}`;
  };

  const StatusPill = ({ status }: { status?: string }) => {
    const st = (status || "").toLowerCase();
    const isClosed = st === "closed" || st === "success" || st === "completed";
    const label = status ? status : "-";
    const cls = isClosed
      ? "text-green-700 border-green-300 bg-green-50"
      : "text-orange-700 border-orange-300 bg-orange-50";
    return (
      <span className={`px-4 py-1 rounded-full border text-xs ${cls}`}>
        {label}
      </span>
    );
  };

  const InvoiceNumberChips = ({ value }: { value: any }) => {
    const invoiceNumbers = getCellValues(value, [
      "invoiceNumber",
      "invoiceNo",
      "invoice_id",
    ]).filter((invoiceNumber, index, values) => {
      return values.indexOf(invoiceNumber) === index;
    });
    const visibleInvoiceNumbers = invoiceNumbers.slice(0, 3);
    const hiddenInvoiceCount =
      invoiceNumbers.length - visibleInvoiceNumbers.length;
    const title = invoiceNumbers.join(", ");

    if (invoiceNumbers.length === 0) {
      return <span className="font-Open text-sm font-normal leading-5">-</span>;
    }

    return (
      <div className="flex max-w-[280px] flex-wrap gap-2" title={title}>
        {visibleInvoiceNumbers.map((invoiceNumber, index) => (
          <span
            key={`${invoiceNumber}-${index}`}
            className="inline-flex max-w-[160px] items-center rounded-full border border-[#BBD7FF] bg-[#F1F7FF] px-3 py-1 text-xs font-medium leading-4 text-[#004C99]"
            title={invoiceNumber}
          >
            <span className="truncate">{invoiceNumber}</span>
          </span>
        ))}
        {hiddenInvoiceCount > 0 && (
          <span
            className="inline-flex items-center rounded-full border border-[#D9D9D9] bg-[#F7F7F7] px-3 py-1 text-xs font-medium leading-4 text-[#494949]"
            title={title}
          >
            +{hiddenInvoiceCount} more
          </span>
        )}
      </div>
    );
  };

  const get = (row: CreditNoteRow, keys: string[]) => {
    for (const k of keys) {
      const v = row?.[k];
      if (v !== undefined && v !== null && v !== "") return v;
    }
    return undefined;
  };

  const billingOrdersHeading = [
    columnsHelper.accessor("creditNoteRaisedOn", {
      header: () => {
        return (
          <div className="flex justify-between ">
            <h1 className="font-Open text-sm font-semibold leading-5  text-[#1C1C1C] self-center ">
              Credit Note Raised On
            </h1>
            <img className="cursor-pointer" src={sortIcon} alt="" />
          </div>
        );
      },

      cell: (info: any) => {
        const raw = get(info?.row?.original, [
          "creditNoteRaisedOn",
          "credit_date",
          "raisedOn",
          "createdAt",
          "date",
        ]);
        return (
          <div className="py-4">
            <p className="font-Open text-sm font-normal leading-5">
              {formatRaisedOn(raw)}
            </p>
          </div>
        );
      },
    }),
    columnsHelper.accessor("creditNoteNumber", {
      header: () => {
        return (
          <div className="flex justify-between ">
            <h1 className="font-Open text-sm font-semibold leading-5 text-[#1C1C1C] self-center ">
              Credit Note Number
            </h1>
            <img className="cursor-pointer" src={sortIcon} alt="" />
          </div>
        );
      },

      cell: (info: any) => {
        const value = get(info?.row?.original, [
          "creditNoteNumber",
          "creditNumber",
          "creditNoteNo",
          "credit_note_number",
          "creditNumber",
          "creditNoteId",
          "creditNote",
        ]);
        return (
          <div className="py-4">
            <p className="font-Open text-sm font-normal leading-5">
              {formatCellValue(value, [
                "creditNoteNumber",
                "creditNoteNo",
                "credit_note_number",
              ])}
            </p>
          </div>
        );
      },
    }),

    columnsHelper.accessor("amount", {
      header: () => {
        return (
          <div className="flex justify-between">
            <p className="font-Open text-sm font-semibold leading-[18px]  text-[#1C1C1C] self-center ">
              Amount
            </p>
            <img className="cursor-pointer" src={sortIcon} alt="" />
          </div>
        );
      },

      cell: (info: any) => {
        const value = get(info?.row?.original, [
          "amount",
          "creditAmount",
          "credit_amount",
          "amt",
        ]);
        return (
          <div className="py-4">
            <p className="font-Open text-sm font-normal leading-5">
              {formatAmount(value)}
            </p>
          </div>
        );
      },
    }),
    columnsHelper.accessor("status", {
      header: () => {
        return (
          <div className="flex justify-between ">
            <p className="font-Open text-sm font-semibold leading-[18px]  text-[#1C1C1C] self-center ">
              Status
            </p>
            <img className="cursor-pointer" src={sortIcon} alt="" />
          </div>
        );
      },

      cell: (info: any) => {
        const value = get(info?.row?.original, [
          "status",
          "state",
          "credit_status",
        ]);
        return (
          <div className="py-4">
            <StatusPill status={formatCellValue(value, ["status", "state"])} />
          </div>
        );
      },
    }),
    columnsHelper.accessor("invoiceNumber", {
      header: () => {
        return (
          <div className="flex justify-between ">
            <p className="font-Open text-sm font-semibold leading-[18px]  text-[#1C1C1C] self-center ">
              Invoice Number
            </p>
          </div>
        );
      },

      cell: (info: any) => {
        const value = get(info?.row?.original, [
          "invoiceNumber",
          "invoiceNo",
          "invoice_id",
        ]);
        return (
          <div className="py-4">
            <InvoiceNumberChips value={value} />
          </div>
        );
      },
    }),
    columnsHelper.accessor("transactionId", {
      header: () => {
        return (
          <div className="flex justify-between ">
            <p className="font-Open text-sm font-semibold leading-[18px]  text-[#1C1C1C] self-center ">
              Transaction ID
            </p>
          </div>
        );
      },
      cell: (info: any) => {
        const value = get(info?.row?.original, [
          "transactionId",
          "transactionID",
          "txnId",
          "transaction_id",
        ]);
        return (
          <div className="py-4">
            <p className="font-Open text-sm font-normal leading-5 break-all">
              {formatCellValue(value, [
                "transactionId",
                "transactionID",
                "txnId",
                "transaction_id",
              ])}
            </p>
          </div>
        );
      },
    }),
  ];

  const columns = useMemo(() => billingOrdersHeading, []);

  return (
    <div>
      <CustomTable
        columnsData={columns}
        rowData={creditData}
      />
    </div>
  );
};

export default CreditNoteData;
